import React, { useState, useEffect, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { setBranchId } from '../../../auth/actions';
import PropTypes from 'prop-types';
import ProductTable from './ProductTable';
import ProductImagePanel from './ProductImagePanel';
import { Button, Input, Dropdown } from '../../../../common/ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFilteredProducts } from '../actions';
import FilterComp from './FilterComp';
import { setFiltersVisibility } from '../../../../common/actions';

const DEFAULT_PAGE = 0;
const DEFAULT_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 400;

const Products = ({
  setBranchId,
  auth: { branchList, branchId },
  commonState,
  setFiltersVisibility,
  getFilteredProducts,
  productsState: { productsData, filteredProductsTotal, isLoading },
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchDebounceRef = useRef(null);

  const [filters, setFilters] = useState({});
  const [supplierResetTrigger, setSupplierResetTrigger] = useState(false);
  const [categoryResetTrigger, setCategoryResetTrigger] = useState(false);
  const [selectedSupplier, setselectedSupplier] = useState(null);
  const [selectedCategory, setselectedCategory] = useState(null);
  const [productType, setProductType] = useState('ALL');
  const [remainingQuantity, setRemainingQuantity] = useState('ALL');
  const [lowStockThreshold, setLowStockThreshold] = useState('');
  const [viewImageProductId, setViewImageProductId] = useState(null);

  const search = searchParams.get('search') ?? '';
  const page = parseInt(searchParams.get('page'), 10);
  const limit = parseInt(searchParams.get('limit'), 10);
  const pageNum = Number.isFinite(page) && page >= 0 ? page : DEFAULT_PAGE;
  const limitNum = Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_LIMIT;

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const onClickNewProduct = () => {
    navigate('/inventory/products/new-product');
  };

  const handleSearchChange = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value != null && value !== '') {
        next.set('search', String(value));
      } else {
        next.delete('search');
      }
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(newPage));
      return next;
    });
  };

  const handleRowsPerPageChange = (valueOrEvent) => {
    const newLimit = typeof valueOrEvent === 'object' && valueOrEvent?.target?.value != null
      ? parseInt(valueOrEvent.target.value, 10)
      : parseInt(valueOrEvent, 10);
    const limitVal = Number.isFinite(newLimit) && newLimit > 0 ? newLimit : DEFAULT_LIMIT;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('limit', String(limitVal));
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handleSetFilters = (newFilters) => {
    setFilters(newFilters);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      if (!(key in prev)) return prev;
      const updated = { ...prev };
      delete updated[key];
      if (key === 'remainingQuantity') delete updated['lowStockThresholdValue'];
      return updated;
    });
    if (key === 'supplier') {
      setSupplierResetTrigger((prev) => !prev);
      setselectedSupplier(null);
    } else if (key === 'category') {
      setCategoryResetTrigger((prev) => !prev);
      setselectedCategory(null);
    } else if (key === 'productType') {
      setProductType('ALL');
    } else if (key === 'remainingQuantity') {
      setRemainingQuantity('ALL');
      setLowStockThreshold('');
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setselectedSupplier(null);
    setselectedCategory(null);
    setProductType('ALL');
    setRemainingQuantity('ALL');
    setLowStockThreshold('');
    setSupplierResetTrigger((prev) => !prev);
    setCategoryResetTrigger((prev) => !prev);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(DEFAULT_PAGE));
      return next;
    });
  };

  useEffect(() => {
    const hasPage = searchParams.has('page');
    const hasLimit = searchParams.has('limit');
    if (!hasPage || !hasLimit) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!hasPage) next.set('page', String(DEFAULT_PAGE));
        if (!hasLimit) next.set('limit', String(DEFAULT_LIMIT));
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to init URL params
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const doFetch = () => {
      getFilteredProducts(search, pageNum, limitNum, filters);
    };

    searchDebounceRef.current = setTimeout(doFetch, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search, pageNum, limitNum, filters, getFilteredProducts, branchId]);

  return (
    <div className={styles.container}>
      <ProductImagePanel
        productId={viewImageProductId}
        branchId={branchId}
        onClose={() => setViewImageProductId(null)}
      />
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Products</h1>
          <p className={styles.breadcrumb}>Inventory / Products</p>
        </div>
        {branchList && branchList.length > 0 && branchId && (
          <div className={styles.branchSelectWrap}>
            <Dropdown
              label="Branch"
              id="branch-select"
              options={branchList.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              value={branchId}
              onChange={handleChangeBranch}
              aria-label="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.chartMainDiv}>
          <div className={styles.topDiv}>
            <div className={styles.leftDiv}>
              <Button onClick={onClickNewProduct}>New Product</Button>
            </div>
            <div className={styles.rightDiv}>
              <Input
                id="search-input"
                placeholder="Search products..."
                type="text"
                value={search}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
              <Button
                variant="secondary"
                onClick={() => setFiltersVisibility(!commonState.isFiltersOpen)}
              >
                Filters
              </Button>
              <FilterComp
                filters={filters}
                setFilters={handleSetFilters}
                supplierResetTrigger={supplierResetTrigger}
                categoryResetTrigger={categoryResetTrigger}
                selectedSupplier={selectedSupplier}
                setselectedSupplier={setselectedSupplier}
                selectedCategory={selectedCategory}
                setselectedCategory={setselectedCategory}
                productType={productType}
                setProductType={setProductType}
                remainingQuantity={remainingQuantity}
                setRemainingQuantity={setRemainingQuantity}
                lowStockThreshold={lowStockThreshold}
                setLowStockThreshold={setLowStockThreshold}
              />
            </div>
          </div>
          <div className={styles.addedFiltersDiv}>
            {Object.entries(filters).map(([key, value]) => {
              if (key === 'lowStockThresholdValue') return null;
              const label =
                key === 'supplier'
                  ? `Supplier: ${value.companyname}`
                  : key === 'category'
                    ? `Category: ${value.name}`
                    : key === 'productType'
                      ? `Type: ${value === 'STOCK' ? 'Stock' : 'Non Stock'}`
                      : key === 'remainingQuantity'
                        ? `Stock: ${{ NON_ZERO: 'Non Zero', ZERO: 'Zero', LOW_STOCK: `Low Stock${filters.lowStockThresholdValue ? ` (≤${filters.lowStockThresholdValue})` : ''}` }[value] || value}`
                        : null;
              if (!label) return null;
              return (
                <span key={key} className={styles.filterTag}>
                  {label}
                  <button
                    type="button"
                    className={styles.removeFilterBtn}
                    onClick={() => handleRemoveFilter(key)}
                    aria-label={`Remove ${key} filter`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
            {Object.keys(filters).length > 0 && (
              <button
                type="button"
                className={styles.resetFiltersBtn}
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            )}
          </div>
          <div className={styles.chartDiv}>
            <ProductTable
              rows={productsData}
              totalCount={filteredProductsTotal}
              page={pageNum}
              rowsPerPage={limitNum}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onViewImage={setViewImageProductId}
              loading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Products.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  getFilteredProducts: PropTypes.func.isRequired,
  setFiltersVisibility: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  commonState: PropTypes.object,
  productsState: PropTypes.shape({
    productsData: PropTypes.array,
    filteredProductsTotal: PropTypes.number,
    isLoading: PropTypes.bool,
  }),
};

Products.defaultProps = {
  auth: {},
  productsState: { productsData: [], filteredProductsTotal: 0, isLoading: false },
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  commonState: state.commonState,
  productsState: state.productsState,
});

export default connect(mapStateToProps, {
  setBranchId,
  getFilteredProducts,
  setFiltersVisibility,
})(Products);
